import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "@emotion/styled";
import {
  CloseOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import { Modal, Spin, message, Button, Table } from "antd";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { CALL_FEATCH_API } from "common/API";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import COLORS from "common/Colors";



/* ─────────────── Styled Components ─────────────── */

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;

  span.active {
    color: #111827;
    font-weight: 600;
  }

  span.sep {
    color: #9ca3af;
  }
`;

const ContentArea = styled.div`
  margin-top: 20px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TotalCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 7px 12px;
  background: #fff;
  width: 220px;

  input {
    border: none;
    outline: none;
    font-size: 13px;
    color: #374151;
    background: transparent;
    width: 100%;
    font-family: inherit;

    &::placeholder {
      color: #9ca3af;
    }
  }

  svg {
    color: #9ca3af;
    font-size: 14px;
    flex-shrink: 0;
  }
`;

/* ─── Antd Table Overrides ─── */

const StyledTableWrapper = styled.div`
  .ant-table {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #f3f4f6;
    font-size: 13px;
  }

  .ant-table-thead > tr > th {
    background: #f9fafb;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    border-bottom: 1.5px solid #f3f4f6;
    padding: 12px 20px;
  }

  .ant-table-tbody > tr > td {
    padding: 14px 20px;
    font-size: 13px;
    color: #111827;
    border-bottom: 1px solid #f3f4f6;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f9fafb !important;
  }

  .ant-table-tbody > tr.dragging-row > td {
    background: #f0fdf4 !important;
    opacity: 0.7;
  }

  /* Hide default antd pagination if not needed */
  .ant-table-pagination {
    display: none;
  }

  /* Row actions: hidden until row hover */
  .row-actions {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s;
  }

  .ant-table-tbody > tr:hover .row-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

const DragHandle = styled.span`
  color: #d1d5db;
  font-size: 14px;
  cursor: grab;
  display: flex;
  align-items: center;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  border: none;
  background: #deefee;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;

  &:hover {
    background: #deefee;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`;

/* ─── Modal ─── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 480px;
  max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  animation: slideUp 0.2s ease;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 10px; }
  .ant-modal-body { padding: 24px 10px 24px 10px; }
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 8px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &::placeholder {
    color: #9ca3af;
  }

  &.error {
    border-color: #ef4444;
  }
`;

const ErrorMsg = styled.span`
  font-size: 14px;
  color: #ef4444;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
`;

const StyledTable = styled(Table)`
  padding-top: 10px;

  table {
    width: 100% !important;
    border: 1px solid #ccc;
  }

  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    color: ${COLORS.TEXT.PRIMARY};
    font-size: 14px;
  }

  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }

  .normal-column {
    min-width: 300px;
    white-space: normal;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.TEXT.PRIMARY};
    white-space: nowrap;
  }

  .non-white {
    background: #f9f9f9;
  }

  .ant-select-selector {
    min-width: 150px;
  }
`;

/* ─────────────── Helpers ─────────────── */

function formatDate(dateStr) {
  if (!dateStr) return "–";
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? "st" :
      day % 10 === 2 && day !== 12 ? "nd" :
        day % 10 === 3 && day !== 13 ? "rd" : "th";
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return `${day}${suffix} ${month} ${year}; ${time}`;
}

/* ─────────────── Category Modal ─────────────── */

function CategoryModal({ mode, initialValue, onClose, onSubmit, submitting, existingNames = [] }) {
  const [value, setValue] = useState(initialValue || "");
  const [error, setError] = useState("");
  const isEdit = mode === "edit";

  const handleSubmit = () => {
    if (!value.trim()) {
      setError("Category name is required");
      return;
    }
    if (existingNames.includes(value.trim().toLowerCase())) {
      setError("A category with this name already exists.");
      return;
    }
    onSubmit(value.trim());
  };

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isEdit ? "Edit Category" : "Creating Category"}</ModalTitle>
          <CloseBtn onClick={onClose} disabled={submitting}><CloseOutlined /></CloseBtn>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <label>Category Name <span style={{ color: "red" }}>*</span></label>
            <TextInput
              autoFocus
              placeholder="e.g. Phone Call"
              value={value}
              className={error ? "error" : ""}
              onChange={(e) => { setValue(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && !submitting && handleSubmit()}
            />
            {error && <ErrorMsg>{error}</ErrorMsg>}
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <ButtonOutlined onClick={onClose} disabled={submitting}>Cancel</ButtonOutlined>
          <ButtonFilled onClick={handleSubmit} disabled={submitting}>
            {submitting && <span className="spinner" />}
            {submitting ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save" : "Create")}
          </ButtonFilled>
        </ModalFooter>
      </ModalBox>
    </Overlay>
  );
}

/* ─────────────── Main CategoriesPage ─────────────── */

const CategoriesPage = ({ onBack }) => {
  const userInfo = useSelector((store) => get(store, "common.userInfo"));
  const userList = useSelector((store) => get(store, "common.userList", []));

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [modalMode, setModalMode] = useState(null); // null | "create" | "edit"
  const [editingCat, setEditingCat] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Delete error modal (active logs > 0)
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);

  // Drag state
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragOverIndex = useRef(null);

  const getUserName = useCallback((idOrEmail) => {
    if (!idOrEmail) return "–";
    const user = userList.find(
      (u) =>
        u.userid === idOrEmail ||
        u.email === idOrEmail ||
        u.id === idOrEmail
    );
    return user?.username || user?.name || user?.fullname || idOrEmail;
  }, [userList]);

  /* ── Fetch categories ────────────────────────────────────────────────── */
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CALL_FEATCH_API("insyts-matter/gettimelogcategory", "get");

      let rows = [];
      if (Array.isArray(response)) {
        console.log(response, "response?.data")
        rows = response;
      } else if (response?.status === "SUCCESS" && Array.isArray(response?.response?.data)) {
        rows = response.response.data;
      } else if (Array.isArray(response?.data)) {
        rows = response.data;
      }

      setCategories(rows);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      message.error("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /* ── Filtered list ───────────────────────────────────────────────────── */
  const filtered = categories.filter((c) =>
    (c.categorytype || c.name || "").toLowerCase().includes(search.toLowerCase())
  );

  /* ── Modal helpers ───────────────────────────────────────────────────── */
  const openCreate = () => { setEditingCat(null); setModalMode("create"); };
  const openEdit = (cat) => { setEditingCat(cat); setModalMode("edit"); };
  const closeModal = () => { if (!submitting) { setModalMode(null); setEditingCat(null); } };

  /* ── Create ──────────────────────────────────────────────────────────── */
  const handleCreate = async (name) => {
    const isDuplicate = categories.some(
      (c) => (c.categorytype || c.name || "").toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      message.error("A category with this name already exists.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        categoryname: name,
        createdby: userInfo?.userid || userInfo?.email || "",
      };
      const response = await CALL_FEATCH_API("insyts-matter/addtimelogcategory", "post", payload);

      if (response?.status === "SUCCESS") {
        message.success("Category created successfully!");
        closeModal();
        fetchCategories();
      } else {
        message.error("Failed to create category. Please try again.");
      }
    } catch (err) {
      console.error("Create category error:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Update ──────────────────────────────────────────────────────────── */
  const handleUpdate = async (name) => {
    try {
      setSubmitting(true);
      const payload = {
        id: String(editingCat.categoryid),
        categoryname: name,
        updatedby: userInfo?.userid || userInfo?.email || "",
      };
      const response = await CALL_FEATCH_API("insyts-matter/updatetimelogcategory", "patch", payload);

      if (response?.status === "SUCCESS") {
        message.success("Category updated successfully!");
        closeModal();
        fetchCategories();
      } else {
        message.error("Failed to update category. Please try again.");
      }
    } catch (err) {
      console.error("Update category error:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Delete ──────────────────────────────────────────────────────────── */
  const handleDelete = async (cat) => {
    // ── RESTRICTION: show error modal if category has active logs ──
    if ((cat.activelogscount ?? 0) > 0) {
      setDeleteErrorOpen(true);
      return;
    }

    const id = cat.categoryid;
    if (!id) { message.error("Cannot delete: ID not found."); return; }

    try {
      setDeletingId(id);
      const payload = { id: String(id) };
      const response = await CALL_FEATCH_API("insyts-matter/deletetimelogcategory", "post", payload);

      if (response?.status ===
        "SUCCESS") {
        message.success("Category deleted successfully!");
        fetchCategories();
      } else {
        message.error("Failed to delete category. Please try again.");
      }
    } catch (err) {
      console.error("Delete category error:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Drag & Drop handlers ────────────────────────────────────────────── */
  const handleDragStart = (index) => setDraggingIndex(index);

  const handleDragOver = (index) => {
    dragOverIndex.current = index;
  };

  const handleDrop = async () => {
    const from = draggingIndex;
    const to = dragOverIndex.current;

    if (from === null || to === null || from === to) return;

    // 👉 STEP 1: clone current list
    const currentList = [...categories];

    // 👉 STEP 2: get dragged items from filtered
    const fromItem = filtered[from];
    const toItem = filtered[to];

    const fromIdx = currentList.findIndex(
      (c) => c.categoryid === fromItem.categoryid
    );
    const toIdx = currentList.findIndex(
      (c) => c.categoryid === toItem.categoryid
    );

    // 👉 STEP 3: reorder
    const [moved] = currentList.splice(fromIdx, 1);
    currentList.splice(toIdx, 0, moved);

    // 👉 STEP 4: update UI
    setCategories(currentList);

    // 👉 STEP 5: prepare payload
    const payload = currentList.map((item, index) => ({
      id: item.categoryid,
      orderindex: index,
    }));

    console.log(payload, "FINAL PAYLOAD"); // 🔍 check this

    // 👉 STEP 6: call API
    try {
      await CALL_FEATCH_API(
        "insyts-matter/reordercategory",
        "patch",
        payload
      );

      message.success("Order updated successfully");
    } catch (err) {
      console.error("Reorder API error:", err);
      message.error("Failed to update order");
    }
  };
  const handleDragEnd = () => {
    setDraggingIndex(null);
    dragOverIndex.current = null;
  };

  const handleSubmit = (name) => {
    if (modalMode === "create") handleCreate(name);
    else if (modalMode === "edit") handleUpdate(name);
  };

  /* ── Antd Table columns ──────────────────────────────────────────────── */
  const columns = [
    {
      key: "drag",
      width: 40,
      render: (_, __, index) => (
        <DragHandle>
          <HolderOutlined />
        </DragHandle>
      ),
    },
    {
      title: "Type",
      key: "type",
      render: (_, cat) => (
        <span style={{ fontWeight: 500 }}>{cat.categorytype || cat.name || "–"}</span>
      ),
    },
    {
      title: "Recent Update by",
      key: "updatedby",
      render: (_, cat) => getUserName(cat.updatedby || cat.createdby),
    },
    {
      title: "Updated on",
      key: "updatedat",
      render: (_, cat) => formatDate(cat.updatedat || cat.createdat),
    },
    {
      title: "Active Logs under",
      key: "activelogscount",
      render: (_, cat) => cat.activelogscount ?? 0,
    },
    {
      key: "actions",
      width: 88,
      render: (_, cat) => (
        <RowActions className="row-actions">
          <ActionBtn
            title="Edit"
            onClick={(e) => { e.stopPropagation(); openEdit(cat); }}
            disabled={deletingId === cat.categoryid}
          >
            <EditOutlined  style={{
                                             color: "#00685F",
                                            cursor: "pointer",
                                            background: "#deefee",
                                            padding: "2px",
                                            borderRadius: "4px",
                                            height: "25px",
                                            width: "25px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 18
                                        }}/>
          </ActionBtn>
          <ActionBtn
            title="Delete"
            onClick={(e) => { e.stopPropagation(); handleDelete(cat); }}
            disabled={deletingId === cat.categoryid}
            style={{ opacity: deletingId === cat.categoryid ? 0.5 : undefined }}
          >
            <DeleteOutlined  style={{
                                             color: "#00685F",
                                            cursor: "pointer",
                                            background: "#deefee",
                                            padding: "2px",
                                            borderRadius: "4px",
                                            height: "25px",
                                            width: "25px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 18
                                        }}/>
          </ActionBtn>
        </RowActions>
      ),
    },
  ];

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb>
        <span style={{ cursor: "pointer" }} onClick={onBack}>
          My History (Time)
        </span>
        <span className="sep">›</span>
        <span className="active">Categories</span>
      </Breadcrumb>

      <ContentArea>
        {/* Toolbar */}
        <Toolbar>
          <TotalCount>Total Categories: {filtered.length}</TotalCount>
          <RightControls>
            <SearchBox>
              <SearchOutlined />
              <input
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
            <ButtonFilled onClick={openCreate} disabled={loading}>
              Create Category
            </ButtonFilled>
          </RightControls>
        </Toolbar>

        {/* Antd Table */}
        {loading ? (
          <LoadingWrapper>
            <Spin size="large" tip="Loading categories..." />
          </LoadingWrapper>
        ) : (
          <StyledTable
            dataSource={filtered}
            columns={columns}
            rowKey={(cat, index) => cat.categoryid ?? index}
            pagination={false}
            locale={{ emptyText: "No categories found." }}
            onRow={(_, index) => ({
              draggable: true,
              className: draggingIndex === index ? "dragging-row" : "",
              onDragStart: () => handleDragStart(index),
              onDragOver: (e) => { e.preventDefault(); handleDragOver(index); },
              onDrop: () => handleDrop(),
              onDragEnd: handleDragEnd,
            })}
          />
        )}
      </ContentArea>

      {/* Create / Edit Modal */}
      {modalMode && (
        <CategoryModal
          mode={modalMode}
          initialValue={modalMode === "edit" ? (editingCat?.categorytype || editingCat?.name || "") : ""}
          onClose={closeModal}
          onSubmit={handleSubmit}
          submitting={submitting}
          existingNames={categories
            .filter((c) =>
              // On edit, exclude the current category's own name
              modalMode !== "edit" || c.categoryid !== editingCat?.categoryid
            )
            .map((c) => (c.categorytype || c.name || "").toLowerCase())}
        />
      )}

      {/* Delete Error Modal — shown when activelogscount > 0 */}
      <StyledModal
        open={deleteErrorOpen}
        onCancel={() => setDeleteErrorOpen(false)}
        footer={null}
        width={420}
        centered
        closable={false}
      >
        <div style={{ textAlign: "center", padding: "0px 14px 8px" }}>
          <div style={{
            width: 35, height: 35, borderRadius: "50%",
            background: "#ff4d4f", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 20px",
            fontSize: 24, color: "white", fontWeight: "bold",
          }}>!</div>
          <p style={{ fontSize: 15, color: "#444", lineHeight: 1.6, marginBottom: 24 }}>
            This category cannot be deleted because it has unbilled logs associated with it.
            Only categories with zero unbilled logs can be deleted.
          </p>
          <Button
            onClick={() => setDeleteErrorOpen(false)}
            style={{ borderRadius: 6, width: 115, height: 34, borderColor: "black" }}
          >
            Got it
          </Button>
        </div>
      </StyledModal>
    </>
  );
};

export default CategoriesPage;